import styled, { css } from "styled-components"
import CloudinaryImage from "components/cloudinary-image"

import { minWidth } from "utils"

export const Container = styled.div`
  display: flex;
  flex-direction: column;

  padding: 3.5rem 0 3.5rem 3.5rem;

  ${minWidth.tablet`
    padding: 6rem 0 6rem 4rem;
  `}
`

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  width: 100%;
  padding-right: 3rem;

  ${minWidth.tablet`
    max-width: 50%;
  `};

  ${minWidth.desktop`
    max-width: 48rem;
  `};

  ${minWidth.desktopXL`
    max-width: 55rem;
  `};
`

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-bottom: 0.05rem solid #000;
`

export const Title = styled.h2`
  font-family: Maax, "Helvetica Neue", Helvetica, Arial, sans-serif;
  text-transform: lowercase;
  font-size: 3rem;
  letter-spacing: -0.02rem;
  font-weight: 400;
  color: #12233f;
  padding: 1rem 0;
  margin: 0;
  flex: 1;

  ${minWidth.tablet`
    font-size: 3.2rem;
    padding: 1.5rem 0;
  `};

  ${minWidth.desktop`
    font-size: 3.4rem;
    padding: 2rem 0;
  `};
`

export const Text = styled.p`
  font-weight: 400;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text.primary};
  letter-spacing: 0;
  margin: 0;

  ${minWidth.tablet`
    font-size: 1.6rem;
  `};

  ${minWidth.desktop`
    font-size: 1.9rem;
  `};
`

export const CardRow = styled.div`
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  } /* WebKit */
  position: relative;
  will-change: scroll-position;

  ${minWidth.desktop`
    gap: 3rem;
  `};
`

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-width: 9.5rem;
  gap: 1rem;
  position: relative;

  &:last-child {
    margin-right: 2rem;
  }

  ${minWidth.tablet`
    min-width: 15rem;

    &:first-child {
      margin-left: 2rem;
    }
  `};

  ${minWidth.desktop`
    min-width: 17.7rem;
  `};
`

export const CardRowTrack = styled.div`
  display: flex;
  gap: 1.5rem;
  width: max-content;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  contain: paint;

  ${minWidth.desktop`
    gap: 3rem;
  `};

  > ${Card}:first-child {
    margin-left: 0;
  }
  > ${Card}:last-child {
    margin-right: 0;
  }
`

export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  overflow: hidden;
  margin: 1rem 0 0 -4.5rem;
  /* Create a stacking context & layer to isolate 3D transforms inside tracks */
  position: relative;
  contain: layout paint style;

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  ${minWidth.tablet`
    gap: 5rem;
    margin: 2rem 0;
  `};

  ${minWidth.desktop`
    gap: 6.5rem;
  `};

  /* Pause marquee while focused for a11y (all devices) */
  ${() => css`
    &:focus-within ${CardRowTrack} {
      animation-play-state: paused;
    }
  `}

  ${() => css`
    @media (prefers-reduced-motion: reduce) {
      ${CardRowTrack} {
        animation: none !important;
        transform: none !important;
      }
    }
  `}
`

export const CardImage = styled(CloudinaryImage)`
  width: 9.5rem;
  height: 9.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #d9d9d9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    image-rendering: auto;
  }

  ${minWidth.tablet`
    width: 14.5rem;
    height: 14.5rem;
    border-radius: 1rem;
  `};

  ${minWidth.desktop`
    width: 17.6rem;
    height: 17.6rem;
  `};
`

export const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;

  ${minWidth.tablet`
    gap: 1.5rem;
  `};
`

export const CardName = styled.h3`
  font-size: 0.83rem;
  line-height: 1.03rem;
  font-weight: 500;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  margin: 0;
  /* Stabilize text rendering during transform scroll */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  /* Subtle text shadow to reduce perceived shimmer during animation */
  text-shadow: 0 0 0.03rem rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;

  ${minWidth.mobile`
    font-size: 0.95rem;
    line-height: 1.15rem;
  `};

  ${minWidth.tablet`
    font-size: 1.1rem;
    line-height: 1.3rem;
  `};

  ${minWidth.desktop`
    font-size: 1.3rem;
    line-height: 1.5rem;
  `};

  ${minWidth.desktopL`
    font-size: 1.4rem;
    line-height: 1.7rem;
  `};
`

export const ClassTitlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const CardClassTitle = styled.p`
  font-weight: 500;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.039rem;
  padding: 0.4rem;
  width: 100%;
  border-radius: 0.31rem;
  background-color: ${({ color }) => color};
  color: #fff;
  margin: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  /* Avoid extra compositing layers on moving text to prevent flicker */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  ${minWidth.mobile`
    font-size: 0.63rem;
  `};

  ${minWidth.tablet`
    font-size: 0.9rem;
    border-radius: 0.4rem;
    letter-spacing: 0.04rem;
  `};

  ${minWidth.desktop`
    font-size: 1rem;
    padding: 0.5rem;
    letter-spacing: 0.05rem;
  `};

  ${minWidth.desktopL`
    font-size: 1.1rem;
  `};

  ${minWidth.desktopXL`
    font-size: 1.2rem;
  `};
`
